import { useState, useEffect, useCallback } from 'react';
import { payrollEnhancedAPI, staffAPI } from '../lib/api';
import { useAuth } from '../lib/auth';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { IndianRupee, FileText, Plus, Wallet, Receipt, Pencil } from 'lucide-react';

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

const PayrollEnhancedPage = () => {
  const { hospital } = useAuth();
  const hospitalId = hospital?.id;
  const [structures, setStructures] = useState([]);
  const [payslips, setPayslips] = useState([]);
  const [staff, setStaff] = useState([]);
  const [view, setView] = useState('structures');
  const [showStructureModal, setShowStructureModal] = useState(false);
  const [editStaff, setEditStaff] = useState(null);
  const [filterMonth, setFilterMonth] = useState(new Date().getMonth() + 1);
  const [filterYear, setFilterYear] = useState(new Date().getFullYear());

  const load = useCallback(async () => {
    try {
      const params = hospitalId ? { hospital_id: hospitalId } : {};
      const [r1, r2, r3] = await Promise.all([
        payrollEnhancedAPI.listStructures(params),
        payrollEnhancedAPI.listPayslips({ ...params, month: filterMonth, year: filterYear }),
        staffAPI.list(),
      ]);
      setStructures(r1.data || []);
      setPayslips(r2.data || []);
      setStaff((r3.data || []).filter(s => !hospitalId || s.hospital_id === hospitalId));
    } catch { toast.error('Failed to load'); }
  }, [hospitalId, filterMonth, filterYear]);

  useEffect(() => { load(); }, [load]);

  const generatePayslip = async (staffId) => {
    try {
      await payrollEnhancedAPI.generatePayslip({ staff_id: staffId, month: filterMonth, year: filterYear });
      toast.success('Payslip generated');
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed');
    }
  };

  const totalGross = structures.reduce((s, x) => s + (x.gross_salary || 0), 0);
  const totalNet = structures.reduce((s, x) => s + (x.net_salary || 0), 0);
  const totalDeductions = structures.reduce((s, x) => s + (x.total_deductions || 0), 0);

  return (
    <div className="space-y-5" data-testid="payroll-enhanced-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Payroll</h1>
          <p className="text-sm text-slate-500 mt-0.5">Salary structures · payslip generation · attendance-linked</p>
        </div>
        <Button onClick={() => { setEditStaff(null); setShowStructureModal(true); }} className="bg-teal-600 hover:bg-teal-700" data-testid="add-structure-btn">
          <Plus size={16} className="mr-1.5" /> Configure Salary
        </Button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <StatCard icon={Wallet} label="Configured Staff" value={structures.length} color="text-teal-600" />
        <StatCard icon={IndianRupee} label="Monthly Gross" value={`₹${totalGross.toLocaleString()}`} color="text-blue-600" />
        <StatCard icon={IndianRupee} label="Total Deductions" value={`₹${totalDeductions.toLocaleString()}`} color="text-red-600" />
        <StatCard icon={IndianRupee} label="Monthly Net Out" value={`₹${totalNet.toLocaleString()}`} color="text-emerald-600" />
      </div>

      {/* Tabs */}
      <div className="flex gap-2 border-b border-slate-200">
        <button onClick={() => setView('structures')} className={`px-4 py-2 text-sm font-medium border-b-2 transition ${view === 'structures' ? 'border-teal-500 text-teal-600' : 'border-transparent text-slate-500'}`} data-testid="tab-structures">
          Salary Structures ({structures.length})
        </button>
        <button onClick={() => setView('payslips')} className={`px-4 py-2 text-sm font-medium border-b-2 transition ${view === 'payslips' ? 'border-teal-500 text-teal-600' : 'border-transparent text-slate-500'}`} data-testid="tab-payslips">
          Payslips ({payslips.length})
        </button>
      </div>

      {view === 'payslips' && (
        <div className="bg-white rounded-xl border border-slate-200 p-4 flex items-center gap-3">
          <Label className="m-0">Month:</Label>
          <Select value={String(filterMonth)} onValueChange={v => setFilterMonth(parseInt(v))}>
            <SelectTrigger className="w-32"><SelectValue /></SelectTrigger>
            <SelectContent>{MONTHS.map((m, i) => <SelectItem key={i + 1} value={String(i + 1)}>{m}</SelectItem>)}</SelectContent>
          </Select>
          <Input type="number" value={filterYear} onChange={e => setFilterYear(parseInt(e.target.value))} className="w-24" />
        </div>
      )}

      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        {view === 'structures' ? (
          <table className="w-full text-sm">
            <thead className="bg-slate-50 border-b">
              <tr>
                <th className="text-left py-3 px-4 text-xs font-semibold text-slate-500 uppercase">Staff</th>
                <th className="text-right py-3 px-4 text-xs font-semibold text-slate-500 uppercase">Basic</th>
                <th className="text-right py-3 px-4 text-xs font-semibold text-slate-500 uppercase">HRA</th>
                <th className="text-right py-3 px-4 text-xs font-semibold text-slate-500 uppercase">Gross</th>
                <th className="text-right py-3 px-4 text-xs font-semibold text-slate-500 uppercase">Deductions</th>
                <th className="text-right py-3 px-4 text-xs font-semibold text-slate-500 uppercase">Net</th>
                <th className="text-right py-3 px-4 text-xs font-semibold text-slate-500 uppercase">Actions</th>
              </tr>
            </thead>
            <tbody>
              {structures.length === 0 ? (
                <tr><td colSpan={7} className="text-center py-12 text-slate-400">No salary structures configured.</td></tr>
              ) : structures.map(s => (
                <tr key={s.staff_id} className="border-b border-slate-50 hover:bg-slate-50/50" data-testid={`structure-row-${s.staff_id}`}>
                  <td className="py-3 px-4">
                    <p className="font-medium text-slate-800">{s.staff_name || s.staff_id}</p>
                    <p className="text-xs text-slate-400">{s.department}</p>
                  </td>
                  <td className="py-3 px-4 text-right">₹{s.basic?.toLocaleString()}</td>
                  <td className="py-3 px-4 text-right">₹{s.hra?.toLocaleString()}</td>
                  <td className="py-3 px-4 text-right font-semibold text-slate-800">₹{s.gross_salary?.toLocaleString()}</td>
                  <td className="py-3 px-4 text-right text-red-600">₹{s.total_deductions?.toLocaleString()}</td>
                  <td className="py-3 px-4 text-right font-semibold text-emerald-600">₹{s.net_salary?.toLocaleString()}</td>
                  <td className="py-3 px-4 text-right">
                    <div className="flex items-center justify-end gap-1">
                      <button onClick={() => { setEditStaff({ id: s.staff_id, name: s.staff_name, department: s.department, structure: s }); setShowStructureModal(true); }} className="p-1.5 rounded-lg hover:bg-slate-100" data-testid={`edit-structure-${s.staff_id}`}>
                        <Pencil size={14} className="text-slate-400" />
                      </button>
                      <button onClick={() => generatePayslip(s.staff_id)} className="p-1.5 rounded-lg hover:bg-teal-50" title="Generate Payslip" data-testid={`gen-payslip-${s.staff_id}`}>
                        <Receipt size={14} className="text-teal-500" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-slate-50 border-b">
              <tr>
                <th className="text-left py-3 px-4 text-xs font-semibold text-slate-500 uppercase">Staff</th>
                <th className="text-center py-3 px-4 text-xs font-semibold text-slate-500 uppercase">Period</th>
                <th className="text-center py-3 px-4 text-xs font-semibold text-slate-500 uppercase">Days Present</th>
                <th className="text-right py-3 px-4 text-xs font-semibold text-slate-500 uppercase">Gross</th>
                <th className="text-right py-3 px-4 text-xs font-semibold text-slate-500 uppercase">Deductions</th>
                <th className="text-right py-3 px-4 text-xs font-semibold text-slate-500 uppercase">Net Pay</th>
                <th className="text-center py-3 px-4 text-xs font-semibold text-slate-500 uppercase">Status</th>
              </tr>
            </thead>
            <tbody>
              {payslips.length === 0 ? (
                <tr><td colSpan={7} className="text-center py-12 text-slate-400">No payslips for {MONTHS[filterMonth - 1]} {filterYear}.</td></tr>
              ) : payslips.map(p => (
                <tr key={p.id} className="border-b border-slate-50 hover:bg-slate-50/50" data-testid={`payslip-${p.id}`}>
                  <td className="py-3 px-4 font-medium text-slate-800">{p.staff_name}</td>
                  <td className="py-3 px-4 text-center text-slate-600">{MONTHS[p.month - 1]} {p.year}</td>
                  <td className="py-3 px-4 text-center">{p.present_days}/{p.total_working_days}</td>
                  <td className="py-3 px-4 text-right">₹{p.gross_salary?.toLocaleString()}</td>
                  <td className="py-3 px-4 text-right text-red-600">₹{p.total_deductions?.toLocaleString()}</td>
                  <td className="py-3 px-4 text-right font-semibold text-emerald-600">₹{p.net_salary?.toLocaleString()}</td>
                  <td className="py-3 px-4 text-center">
                    <span className="text-xs px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700">{p.status}</span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <StructureModal open={showStructureModal} onClose={() => setShowStructureModal(false)} editStaff={editStaff} staff={staff} hospitalId={hospitalId} onSaved={load} />
    </div>
  );
};

const StatCard = ({ icon: Icon, label, value, color }) => (
  <div className="bg-white rounded-xl border border-slate-200 p-4">
    <div className="flex items-center gap-2 text-xs text-slate-500 uppercase font-semibold mb-1.5"><Icon size={14} />{label}</div>
    <p className={`text-xl font-bold ${color}`}>{value}</p>
  </div>
);

const StructureModal = ({ open, onClose, editStaff, staff, hospitalId, onSaved }) => {
  const [staffId, setStaffId] = useState('');
  const [form, setForm] = useState({ basic: 0, hra: 0, da: 0, special_allowance: 0, medical: 0, conveyance: 0, shift_allowance: 0, pf_deduction: 0, esi_deduction: 0, tds: 0, professional_tax: 0, loan_emi: 0 });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    if (editStaff?.structure) {
      setStaffId(editStaff.id);
      setForm({
        basic: editStaff.structure.basic || 0,
        hra: editStaff.structure.hra || 0,
        da: editStaff.structure.da || 0,
        special_allowance: editStaff.structure.special_allowance || 0,
        medical: editStaff.structure.medical || 0,
        conveyance: editStaff.structure.conveyance || 0,
        shift_allowance: editStaff.structure.shift_allowance || 0,
        pf_deduction: editStaff.structure.pf_deduction || 0,
        esi_deduction: editStaff.structure.esi_deduction || 0,
        tds: editStaff.structure.tds || 0,
        professional_tax: editStaff.structure.professional_tax || 0,
        loan_emi: editStaff.structure.loan_emi || 0,
      });
    } else {
      setStaffId('');
      setForm({ basic: 0, hra: 0, da: 0, special_allowance: 0, medical: 0, conveyance: 0, shift_allowance: 0, pf_deduction: 0, esi_deduction: 0, tds: 0, professional_tax: 0, loan_emi: 0 });
    }
  }, [editStaff, open]);

  const earnings = ['basic', 'hra', 'da', 'special_allowance', 'medical', 'conveyance', 'shift_allowance'].reduce((s, k) => s + (parseFloat(form[k]) || 0), 0);
  const deductions = ['pf_deduction', 'esi_deduction', 'tds', 'professional_tax', 'loan_emi'].reduce((s, k) => s + (parseFloat(form[k]) || 0), 0);
  const net = earnings - deductions;

  const save = async () => {
    if (!staffId) { toast.error('Pick staff'); return; }
    setSaving(true);
    try {
      const s = staff.find(x => x.id === staffId);
      const payload = {
        staff_id: staffId,
        hospital_id: hospitalId,
        staff_name: s ? `${s.first_name} ${s.last_name}` : '',
        department: s?.department || '',
        ...Object.fromEntries(Object.entries(form).map(([k, v]) => [k, parseFloat(v) || 0])),
      };
      await payrollEnhancedAPI.setStructure(payload);
      toast.success('Salary structure saved'); onSaved(); onClose();
    } catch { toast.error('Failed'); }
    setSaving(false);
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader><DialogTitle>{editStaff ? `Edit Salary — ${editStaff.name}` : 'Configure Salary Structure'}</DialogTitle></DialogHeader>
        <div className="space-y-4 mt-2">
          {!editStaff && (
            <div>
              <Label>Staff *</Label>
              <Select value={staffId} onValueChange={setStaffId}>
                <SelectTrigger data-testid="payroll-staff-select"><SelectValue placeholder="Select staff" /></SelectTrigger>
                <SelectContent>{staff.map(s => <SelectItem key={s.id} value={s.id}>{s.first_name} {s.last_name} · {s.department}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          )}

          <div className="bg-emerald-50/50 rounded-lg p-3">
            <h4 className="text-xs font-bold text-emerald-700 uppercase mb-2">Earnings</h4>
            <div className="grid grid-cols-3 gap-2">
              {['basic', 'hra', 'da', 'special_allowance', 'medical', 'conveyance', 'shift_allowance'].map(k => (
                <div key={k}><Label className="text-xs capitalize">{k.replace(/_/g, ' ')}</Label><Input type="number" value={form[k]} onChange={e => setForm({ ...form, [k]: e.target.value })} data-testid={`pay-${k}`} /></div>
              ))}
            </div>
            <p className="text-xs mt-2 text-emerald-700 font-semibold">Total Earnings: ₹{earnings.toLocaleString()}</p>
          </div>

          <div className="bg-red-50/50 rounded-lg p-3">
            <h4 className="text-xs font-bold text-red-700 uppercase mb-2">Deductions</h4>
            <div className="grid grid-cols-3 gap-2">
              {['pf_deduction', 'esi_deduction', 'tds', 'professional_tax', 'loan_emi'].map(k => (
                <div key={k}><Label className="text-xs capitalize">{k.replace(/_/g, ' ')}</Label><Input type="number" value={form[k]} onChange={e => setForm({ ...form, [k]: e.target.value })} /></div>
              ))}
            </div>
            <p className="text-xs mt-2 text-red-700 font-semibold">Total Deductions: ₹{deductions.toLocaleString()}</p>
          </div>

          <div className="bg-slate-100 rounded-lg p-3 text-center">
            <p className="text-xs text-slate-500 uppercase font-semibold">Net Salary</p>
            <p className="text-2xl font-bold text-slate-800">₹{net.toLocaleString()}</p>
          </div>

          <div className="flex justify-end gap-2 pt-2">
            <Button variant="outline" onClick={onClose}>Cancel</Button>
            <Button onClick={save} disabled={saving} className="bg-teal-600 hover:bg-teal-700" data-testid="save-structure-btn">{saving ? 'Saving...' : 'Save'}</Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default PayrollEnhancedPage;
